import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useToast } from '@/hooks/use-toast';
import { useMetros } from '@/hooks/useReferenceData';
import { useVisibility } from '@/hooks/useVisibility';
import { Plus, Pencil, MapPin, Smartphone } from 'lucide-react';
import { InviteUserDialog } from '@/components/settings/InviteUserDialog';
import { useUserRole } from '@/hooks/useUserRole';
import { z } from 'zod';

const techSchema = z.object({
  name: z.string().min(1, 'Name is required').max(100),
  phone: z.string().optional().or(z.literal('')),
  email: z.string().email('Valid email required').optional().or(z.literal('')),
  commission_percentage: z.number().min(0).max(100, 'Must be between 0 and 100').optional(),
  notes: z.string().optional(),
  contract_signed: z.boolean(),
  contract_signed_date: z.string().optional().or(z.literal('')),
  owns_tools: z.boolean(),
  tools_purchased_date: z.string().optional().or(z.literal('')),
  pays_for_vehicle: z.boolean(),
});

type Tech = {
  id: string;
  name: string;
  phone: string | null;
  email: string | null;
  commission_percentage?: number;
  is_active: boolean;
  notes: string | null;
  contract_signed: boolean;
  contract_signed_date: string | null;
  owns_tools: boolean;
  tools_purchased_date: string | null;
  pays_for_vehicle: boolean;
  /** auth user of the technician's Steddy app login; NULL until a tech invite is accepted. */
  user_id?: string | null;
  tech_metros: { metro_id: string }[];
};

type AppLoginState = 'active' | 'pending' | 'none';

const APP_LOGIN_BADGE: Record<AppLoginState, { label: string; variant: 'success' | 'warning' | 'outline' }> = {
  active: { label: 'App login active', variant: 'success' },
  pending: { label: 'Invite pending', variant: 'warning' },
  none: { label: 'Not invited', variant: 'outline' },
};

export function Technicians() {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingTech, setEditingTech] = useState<Tech | null>(null);
  const [name, setName] = useState('');
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [commission, setCommission] = useState('20');
  const [notes, setNotes] = useState('');
  const [contractSigned, setContractSigned] = useState(false);
  const [contractSignedDate, setContractSignedDate] = useState('');
  const [ownsTools, setOwnsTools] = useState(false);
  const [toolsPurchasedDate, setToolsPurchasedDate] = useState('');
  const [paysForVehicle, setPaysForVehicle] = useState(false);
  const [selectedMetros, setSelectedMetros] = useState<string[]>([]);
  // Active techs are the working list; inactive ones stay one click away.
  const [statusFilter, setStatusFilter] = useState<'active' | 'inactive'>('active');
  // 'Invite to app' opens the shared invite dialog locked to this technician.
  const [inviteTech, setInviteTech] = useState<Tech | null>(null);

  const { toast } = useToast();
  const queryClient = useQueryClient();
  const { data: metros = [] } = useMetros(true);
  const { canSeeTechPay } = useVisibility();
  const { isAdmin } = useUserRole();

  const { data: techs = [], isLoading } = useQuery({
    queryKey: ['techs-settings', canSeeTechPay],
    queryFn: async () => {
      if (canSeeTechPay) {
        const { data, error } = await supabase
          .from('techs')
          .select('*, tech_metros(metro_id)')
          .order('name');
        if (error) throw error;
        return data as Tech[];
      }
      const { data, error } = await supabase
        .from('techs')
        .select('id, name, phone, email, is_active, user_id, notes, contract_signed, contract_signed_date, owns_tools, tools_purchased_date, pays_for_vehicle, tech_metros(metro_id)')
        .order('name');
      if (error) throw error;
      return data as Tech[];
    },
  });

  // Pending app invites per technician (invites are admin-readable; others just see no 'pending').
  const { data: pendingInviteTechIds = new Set<string>() } = useQuery({
    queryKey: ['invites', 'pending-tech'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invites')
        .select('tech_id')
        .eq('status', 'pending')
        .not('tech_id', 'is', null);
      if (error) throw error;
      return new Set((data ?? []).map((i) => i.tech_id as string));
    },
    enabled: isAdmin,
  });

  const appLoginState = (tech: Tech): AppLoginState =>
    tech.user_id ? 'active' : pendingInviteTechIds.has(tech.id) ? 'pending' : 'none';

  // Non-admins never send commission_percentage: new techs get the DB default
  // and edits leave the existing rate untouched.
  const commissionPayload = (values: z.infer<typeof techSchema>) =>
    values.commission_percentage !== undefined ? { commission_percentage: values.commission_percentage } : {};

  // Build metro name lookup for display
  const metroNameMap = new Map(metros.map(m => [m.id, m.name]));

  const activeTechs = techs.filter(t => t.is_active);
  const inactiveTechs = techs.filter(t => !t.is_active);
  const visibleTechs = statusFilter === 'active' ? activeTechs : inactiveTechs;

  const saveMetros = async (techId: string) => {
    const { error } = await supabase.rpc('set_tech_metros', {
      p_tech_id: techId,
      p_metro_ids: selectedMetros,
    });
    if (error) throw error;
  };

  const createMutation = useMutation({
    mutationFn: async (values: z.infer<typeof techSchema>) => {
      const { data, error } = await supabase
        .from('techs')
        .insert({
          name: values.name,
          phone: values.phone || null,
          email: values.email || null,
          ...commissionPayload(values),
          notes: values.notes || null,
          contract_signed: values.contract_signed,
          contract_signed_date: values.contract_signed_date || null,
          owns_tools: values.owns_tools,
          tools_purchased_date: values.tools_purchased_date || null,
          pays_for_vehicle: values.pays_for_vehicle,
        } as any)
        .select('id')
        .single();
      if (error) throw error;
      await saveMetros(data.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['techs-settings'] });
      queryClient.invalidateQueries({ queryKey: ['techs'] });
      queryClient.invalidateQueries({ queryKey: ['techs-with-metros'] });
      toast({ title: 'Technician created successfully' });
      resetForm();
    },
    onError: (error) => {
      toast({ title: 'Error creating technician', description: error.message, variant: 'destructive' });
    },
  });

  const updateMutation = useMutation({
    mutationFn: async ({ id, values }: { id: string; values: z.infer<typeof techSchema> }) => {
      const { error } = await supabase
        .from('techs')
        .update({
          name: values.name,
          phone: values.phone || null,
          email: values.email || null,
          ...commissionPayload(values),
          notes: values.notes || null,
          contract_signed: values.contract_signed,
          contract_signed_date: values.contract_signed_date || null,
          owns_tools: values.owns_tools,
          tools_purchased_date: values.tools_purchased_date || null,
          pays_for_vehicle: values.pays_for_vehicle,
        })
        .eq('id', id);
      if (error) throw error;
      await saveMetros(id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['techs-settings'] });
      queryClient.invalidateQueries({ queryKey: ['techs'] });
      queryClient.invalidateQueries({ queryKey: ['techs-with-metros'] });
      toast({ title: 'Technician updated successfully' });
      resetForm();
    },
    onError: (error) => {
      toast({ title: 'Error updating technician', description: error.message, variant: 'destructive' });
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: async ({ id, isActive }: { id: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('techs')
        .update({ is_active: !isActive })
        .eq('id', id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['techs-settings'] });
      queryClient.invalidateQueries({ queryKey: ['techs'] });
      queryClient.invalidateQueries({ queryKey: ['techs-with-metros'] });
      toast({ title: 'Technician status updated' });
    },
  });

  const resetForm = () => {
    setName('');
    setPhone('');
    setEmail('');
    setCommission('20');
    setNotes('');
    setContractSigned(false);
    setContractSignedDate('');
    setOwnsTools(false);
    setToolsPurchasedDate('');
    setPaysForVehicle(false);
    setSelectedMetros([]);
    setEditingTech(null);
    setIsDialogOpen(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    try {
      const validated = techSchema.parse({
        name,
        phone,
        email,
        commission_percentage: canSeeTechPay ? parseFloat(commission) : undefined,
        notes,
        contract_signed: contractSigned,
        contract_signed_date: contractSignedDate,
        owns_tools: ownsTools,
        tools_purchased_date: toolsPurchasedDate,
        pays_for_vehicle: paysForVehicle,
      });
      if (editingTech) {
        updateMutation.mutate({ id: editingTech.id, values: validated });
      } else {
        createMutation.mutate(validated);
      }
    } catch (error) {
      if (error instanceof z.ZodError) {
        toast({ title: 'Validation error', description: error.errors[0].message, variant: 'destructive' });
      }
    }
  };

  const handleEdit = (tech: Tech) => {
    setEditingTech(tech);
    setName(tech.name);
    setPhone(tech.phone || '');
    setEmail(tech.email || '');
    setCommission(tech.commission_percentage?.toString() ?? '20');
    setNotes(tech.notes || '');
    setContractSigned(tech.contract_signed);
    setContractSignedDate(tech.contract_signed_date || '');
    setOwnsTools(tech.owns_tools);
    setToolsPurchasedDate(tech.tools_purchased_date || '');
    setPaysForVehicle(tech.pays_for_vehicle);
    setSelectedMetros((tech.tech_metros ?? []).map(tm => tm.metro_id));
    setIsDialogOpen(true);
  };

  const toggleMetro = (metroId: string) => {
    setSelectedMetros(prev =>
      prev.includes(metroId) ? prev.filter(id => id !== metroId) : [...prev, metroId]
    );
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold">Technicians</h3>
          <p className="text-sm text-muted-foreground">
            {canSeeTechPay ? 'Manage your field technicians and their commission rates' : 'Manage your field technicians'}
          </p>
        </div>
        <Dialog open={isDialogOpen} onOpenChange={(open) => { setIsDialogOpen(open); if (!open) resetForm(); }}>
          <DialogTrigger asChild>
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              Add Technician
            </Button>
          </DialogTrigger>
          <DialogContent className="max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingTech ? 'Edit Technician' : 'New Technician'}</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name">Name *</Label>
                <Input
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., John Smith"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="phone">Phone</Label>
                <Input
                  id="phone"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  placeholder="0400 000 000"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="tech@example.com"
                />
              </div>
              {canSeeTechPay && (
                <div className="space-y-2">
                  <Label htmlFor="commission">Commission % *</Label>
                  <Input
                    id="commission"
                    type="number"
                    step="0.01"
                    min="0"
                    max="100"
                    value={commission}
                    onChange={(e) => setCommission(e.target.value)}
                    placeholder="20.00"
                    required
                  />
                </div>
              )}
              <div className="space-y-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder="Optional notes"
                  rows={3}
                />
              </div>

              {metros.length > 0 && (
                <div className="space-y-3 border-t pt-4">
                  <div>
                    <Label className="flex items-center gap-1.5">
                      <MapPin className="h-3.5 w-3.5" />
                      Metro Assignments
                    </Label>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      Restricts which metro's jobs this tech appears in when a CSR books a job. Leave all unchecked to allow booking in any metro. Has no effect on reports — reports always group by job location.
                    </p>
                  </div>
                  <div className="grid grid-cols-2 gap-2">
                    {metros.map(metro => (
                      <div key={metro.id} className="flex items-center gap-2">
                        <Checkbox
                          id={`metro-${metro.id}`}
                          checked={selectedMetros.includes(metro.id)}
                          onCheckedChange={() => toggleMetro(metro.id)}
                        />
                        <Label htmlFor={`metro-${metro.id}`} className="cursor-pointer font-normal">
                          {metro.name}
                        </Label>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-4 border-t pt-4">
                <h4 className="text-sm font-medium">Additional Information</h4>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="contractSigned"
                    checked={contractSigned}
                    onCheckedChange={(checked) => setContractSigned(checked as boolean)}
                  />
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="contractSigned" className="cursor-pointer">
                      Contract Signed
                    </Label>
                    {contractSigned && (
                      <Input
                        type="date"
                        value={contractSignedDate}
                        onChange={(e) => setContractSignedDate(e.target.value)}
                        placeholder="Contract signed date"
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-start gap-3">
                  <Checkbox
                    id="ownsTools"
                    checked={ownsTools}
                    onCheckedChange={(checked) => setOwnsTools(checked as boolean)}
                  />
                  <div className="flex-1 space-y-2">
                    <Label htmlFor="ownsTools" className="cursor-pointer">
                      Owns Tools
                    </Label>
                    {ownsTools && (
                      <Input
                        type="date"
                        value={toolsPurchasedDate}
                        onChange={(e) => setToolsPurchasedDate(e.target.value)}
                        placeholder="Tools purchased date"
                      />
                    )}
                  </div>
                </div>

                <div className="flex items-center gap-3">
                  <Checkbox
                    id="paysForVehicle"
                    checked={paysForVehicle}
                    onCheckedChange={(checked) => setPaysForVehicle(checked as boolean)}
                  />
                  <Label htmlFor="paysForVehicle" className="cursor-pointer">
                    Pays for Vehicle
                  </Label>
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={resetForm}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingTech ? 'Update' : 'Create'}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {!isLoading && techs.length > 0 && (
        <Tabs value={statusFilter} onValueChange={(v) => setStatusFilter(v as 'active' | 'inactive')}>
          <TabsList>
            <TabsTrigger value="active">Active ({activeTechs.length})</TabsTrigger>
            <TabsTrigger value="inactive">Inactive ({inactiveTechs.length})</TabsTrigger>
          </TabsList>
        </Tabs>
      )}

      {isLoading ? (
        <div className="text-center py-8">Loading...</div>
      ) : techs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          No technicians yet. Click "Add Technician" to create one.
        </div>
      ) : visibleTechs.length === 0 ? (
        <div className="text-center py-8 text-muted-foreground">
          {statusFilter === 'active'
            ? 'No active technicians. Switch to "Inactive" to reactivate one.'
            : 'No inactive technicians.'}
        </div>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Phone</TableHead>
              <TableHead>Email</TableHead>
              {canSeeTechPay && <TableHead>Commission %</TableHead>}
              <TableHead>Metros</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>App login</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {visibleTechs.map((tech) => {
              const assignedMetros = (tech.tech_metros ?? [])
                .map(tm => metroNameMap.get(tm.metro_id))
                .filter(Boolean) as string[];
              return (
                <TableRow key={tech.id}>
                  <TableCell className="font-medium">{tech.name}</TableCell>
                  <TableCell>{tech.phone || '-'}</TableCell>
                  <TableCell className="text-muted-foreground">{tech.email || '-'}</TableCell>
                  {canSeeTechPay && <TableCell>{tech.commission_percentage}%</TableCell>}
                  <TableCell>
                    {assignedMetros.length === 0 ? (
                      <span className="text-sm text-muted-foreground">All metros</span>
                    ) : (
                      <div className="flex flex-wrap gap-1">
                        {assignedMetros.map(metro => (
                          <Badge key={metro} variant="secondary">{metro}</Badge>
                        ))}
                      </div>
                    )}
                  </TableCell>
                  <TableCell>
                    <Badge variant={tech.is_active ? 'default' : 'secondary'}>
                      {tech.is_active ? 'Active' : 'Inactive'}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <Badge variant={APP_LOGIN_BADGE[appLoginState(tech)].variant}>
                      {APP_LOGIN_BADGE[appLoginState(tech)].label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      {isAdmin && tech.is_active && appLoginState(tech) !== 'active' && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="gap-1"
                          onClick={() => setInviteTech(tech)}
                          title={appLoginState(tech) === 'pending'
                            ? 'Send another invite (the previous one stays valid until it expires)'
                            : 'Email this technician a link to create their Steddy app login'}
                        >
                          <Smartphone className="h-4 w-4" />
                          {appLoginState(tech) === 'pending' ? 'Re-invite' : 'Invite to app'}
                        </Button>
                      )}
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => handleEdit(tech)}
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleActiveMutation.mutate({ id: tech.id, isActive: tech.is_active })}
                      >
                        {tech.is_active ? 'Deactivate' : 'Activate'}
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      {isAdmin && (
        <InviteUserDialog
          open={!!inviteTech}
          onOpenChange={(open) => { if (!open) setInviteTech(null); }}
          defaultRole="tech"
          defaultTechId={inviteTech?.id ?? null}
          defaultEmail={inviteTech?.email ?? ''}
        />
      )}
    </div>
  );
}
